import { BaseQueryApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { clearTokens, setTokens } from "@src/app/authSlice";
import { clearUserDetails, setGlobalState } from "@src/app/globalSlice";
import { RootState } from "@src/app/store";
import { buildForwardedForHeader } from "@src/services/mediaRuntime";

const BASE_URL: string = import.meta.env.VITE_TEST_FRESHERS_BASE_URL;
// const BASE_URL = "http://192.168.0.214:9090/v1/";

const sleep = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const getRequestMethod = (args: unknown): string => {
  if (typeof args === "string") return "GET";
  const method = (args as { method?: string })?.method;
  return (method || "GET").toUpperCase();
};

const isAborted = (api: BaseQueryApi, result?: any): boolean => {
  if (api.signal?.aborted) return true;
  const err = result?.error;
  if (!err) return false;
  if (err.name === "AbortError" || err.error === "AbortError") return true;
  if (
    typeof err.error === "string" &&
    (err.error.includes("aborted") || err.error.includes("AbortError"))
  ) {
    return true;
  }
  return false;
};

const isRetryableStatus = (
  status: unknown,
  api?: BaseQueryApi,
  result?: any,
): boolean => {
  if (api && isAborted(api, result)) return false;
  return (
    status === "FETCH_ERROR" ||
    status === "TIMEOUT_ERROR" ||
    (typeof status === "number" && status >= 500)
  );
};

export const FAST_FETCH_FAILURE_MS = 2500;
export const SECURE_REACH_MESSAGE =
  "Cannot reach the server securely. Check the connection or site certificate, then submit again.";
export const SLOW_NETWORK_MESSAGE =
  "Your connection is slow or timed out. Your answer was saved on this device. Please tap Submit again.";
export const OFFLINE_MESSAGE =
  "You are offline. Please check your internet connection and reconnect.";

export const classifyFetchFailure = (elapsedMs: number, status?: unknown) => {
  const isOnline = typeof navigator !== "undefined" ? navigator.onLine : true;
  if (!isOnline) {
    return { message: OFFLINE_MESSAGE, isSecureReachFailure: false };
  }
  if (status === "TIMEOUT_ERROR" || elapsedMs > FAST_FETCH_FAILURE_MS) {
    return {
      message:
        "Your network connection is slow or timed out. Please check your Wi-Fi/LAN connection and retry.",
      isSecureReachFailure: false,
    };
  }
  return { message: SECURE_REACH_MESSAGE, isSecureReachFailure: true };
};

const baseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  credentials: "include",
  timeout: 60000,
  prepareHeaders: async (headers, { getState }) => {
    // const accessToken = localStorage.getItem("accessToken");
    const state = getState() as RootState;
    const accessToken = state.authSlice.accessToken ?? "";

    if (accessToken) {
      headers.set("Authorization", `Bearer ${accessToken}`);
    }

    if (
      typeof window !== "undefined" &&
      (window as any).electronApp &&
      (window as any).electronApp.getIPInfo
    ) {
      try {
        const ipInfo = await (window as any).electronApp.getIPInfo();
        // Send every local IP so the backend can CIDR-match the real
        // physical NIC, not just the first (possibly virtual) adapter.
        const localIPs = buildForwardedForHeader(ipInfo);
        if (localIPs) {
          headers.set("X-Forwarded-For", localIPs);
        }
      } catch (e) {
        console.error("Failed to get IP info from electronApp", e);
      }
    }

    return headers;
  },
});

function getRefreshToken(): string | null {
  return localStorage.getItem("refreshToken");
}

// Store the ongoing refresh request
let refreshPromise: Promise<string | null> | null = null;

const refreshAccessToken = async (
  api: BaseQueryApi,
  extraOptions: object,
): Promise<string | null> => {
  if (!refreshPromise) {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      // api.dispatch(clearTokens());
      // api.dispatch(clearUserDetails());
      return null;
    }

    // Use baseQuery but explicitly remove headers
    refreshPromise = fetchBaseQuery({
      baseUrl: BASE_URL, // Create a separate baseQuery instance for refresh
      credentials: "include",
    })(
      {
        url: "auth/refresh-token",
        method: "POST",
        body: { refreshToken },
        signal: api.signal,
      },
      api,
      extraOptions,
    )
      .then((refreshResult) => {
        refreshPromise = null;
        if (refreshResult.data && refreshResult.data.response.accessToken) {
          api.dispatch(setTokens(refreshResult.data.response));
          return refreshResult.data.response.accessToken;
        } else {
          api.dispatch(clearTokens());
          api.dispatch(clearUserDetails());
          return null;
        }
      })
      .catch(() => {
        refreshPromise = null;
        return null;
      });
  }

  return refreshPromise;
};

const baseQueryWithReauth = async (args, api, extraOptions) => {
  if (api.signal?.aborted) {
    return {
      error: {
        status: "CUSTOM_ERROR",
        error: "Query was aborted",
      },
    };
  }

  // Forward abort signal
  const requestArgs = { ...args, signal: api.signal };

  const startTime = performance.now();
  let result = await baseQuery(requestArgs, api, extraOptions);

  if (isAborted(api, result)) {
    return result;
  }

  const method = getRequestMethod(args);
  const firstElapsedMs = performance.now() - startTime;
  const firstFailure =
    result.error?.status === "FETCH_ERROR" ||
    result.error?.status === "TIMEOUT_ERROR"
      ? classifyFetchFailure(firstElapsedMs, result.error.status)
      : null;
  const skipRetry = Boolean(firstFailure?.isSecureReachFailure);

  if (
    method === "GET" &&
    result.error &&
    isRetryableStatus(result.error.status, api, result) &&
    !skipRetry
  ) {
    await sleep(600);
    if (isAborted(api)) return result;
    result = await baseQuery(requestArgs, api, extraOptions);
    if (
      result.error &&
      isRetryableStatus(result.error.status, api, result) &&
      !isAborted(api)
    ) {
      await sleep(1200);
      if (isAborted(api)) return result;
      result = await baseQuery(requestArgs, api, extraOptions);
    }
  }

  if (isAborted(api, result)) {
    return result;
  }

  const elapsedMs = performance.now() - startTime;

  if (
    (result.error?.status === "FETCH_ERROR" ||
      result.error?.status === "TIMEOUT_ERROR") &&
    !isAborted(api, result)
  ) {
    const failure = skipRetry
      ? firstFailure
      : classifyFetchFailure(elapsedMs, result.error.status);
    result.error.data = {
      message: failure?.message,
      isNetworkIssue: true,
      isSecureReachFailure: Boolean(failure?.isSecureReachFailure),
      originalError: result.error.data,
    };
  }

  if (result.error?.status === 401) {
    const newToken = await refreshAccessToken(api, extraOptions);

    if (newToken) {
      const retryArgs = {
        ...args,
        signal: api.signal,
        headers: {
          ...(args.headers || {}),
          Authorization: `Bearer ${newToken}`,
        },
      };

      result = await baseQuery(retryArgs, api, extraOptions);
    } else {
      api.dispatch(clearTokens());
      api.dispatch(clearUserDetails());
      window.location.replace("/");
    }
  }

  // Session expired or multiple login detected
  const checkSessionError = (data: any) => {
    if (typeof data === "string") {
      const lower = data.toLowerCase();
      if (
        lower === "session expired" ||
        lower.includes("session expired") ||
        lower.includes("other person logged in") ||
        lower.includes("new login session detected") ||
        lower.includes("session is logged out")
      ) {
        return true;
      }
    } else if (data && typeof data === "object") {
      const str = JSON.stringify(data).toLowerCase();
      if (
        str.includes("session expired") ||
        str.includes("other person logged in") ||
        str.includes("new login session detected") ||
        str.includes("session is logged out")
      ) {
        return true;
      }
    }
    return false;
  };

  if (checkSessionError(result?.error?.data)) {
    api.dispatch(setGlobalState({ loginStatus: "logout" }));
  }

  // Also check result.data in case the API returned 200 OK with the error message as a Blob or text
  if (result?.data instanceof Blob) {
    // Only read small blobs that could be error messages
    if (result.data.size < 1000) {
      try {
        const text = await result.data.text();
        if (checkSessionError(text)) {
          api.dispatch(setGlobalState({ loginStatus: "logout" }));
        }
      } catch (e) {
        console.error(e);
      }
    }
  } else if (checkSessionError(result?.data)) {
    api.dispatch(setGlobalState({ loginStatus: "logout" }));
  }

  return result;
};

export { baseQueryWithReauth };
